import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, RotateCw, Check, X, Coins, Zap, Sigma, BookText, FileCheck2, PartyPopper } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { MathText } from "../components/MathText";

const TYPE_META = {
  formula: { icon: Sigma, color: "#00E5FF", label: "Formula" },
  definizione: { icon: BookText, color: "#6C63FF", label: "Definizione" },
  dimostrazione: { icon: FileCheck2, color: "#FF007F", label: "Dimostrazione" },
};

export default function Flashcards() {
  const { examId, topicId } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const t = useT();
  const [cards, setCards] = useState(null);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [results, setResults] = useState([]);
  const [done, setDone] = useState(null);
  const [onlyUnknown, setOnlyUnknown] = useState(false);

  const load = (unknown) => api.get(`/exams/${examId}/memory/${topicId}?only_unknown=${unknown}`).then((r) => {
    setCards(r.data.cards); setIdx(0); setFlipped(false); setResults([]); setDone(null);
  }).catch(() => setCards([]));
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [examId, topicId]);

  const mark = (known) => {
    const card = cards[idx];
    const newResults = [...results, { card_id: card.card_id, known }];
    setResults(newResults);
    if (idx + 1 >= cards.length) submit(newResults);
    else { setIdx(idx + 1); setFlipped(false); }
  };

  const submit = async (res) => {
    try { const { data } = await api.post("/memory/review", { results: res }); setUser(data.user); setDone(data); }
    catch { setDone({ known: res.filter((r) => r.known).length, coins_earned: 0, xp_earned: 0 }); }
  };

  if (!cards) return <div className="space-bg min-h-screen flex items-center justify-center text-white/60">{t("Caricamento...")}</div>;

  if (cards.length === 0) {
    return (
      <div className="space-bg min-h-screen flex flex-col items-center justify-center px-5 text-center">
        <PartyPopper size={44} style={{ color: "var(--success)" }} className="mb-3" />
        <p className="text-white/70 mb-5">{onlyUnknown ? t("Hai imparato tutte le carte! 🎉") : t("Nessuna carta per questo argomento.")}</p>
        <button onClick={() => navigate(`/exam/${examId}`)} className="px-6 py-3 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="flash-back-empty">{t("Torna all'esame")}</button>
      </div>
    );
  }

  if (done) {
    const unknownCount = results.filter((r) => !r.known).length;
    return (
      <div className="space-bg min-h-screen flex items-center justify-center px-5">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="glass rounded-3xl p-8 max-w-md text-center" data-testid="flash-done-card">
          <h2 className="font-heading font-black text-2xl tracking-tight mb-2">{t("Sessione completata!")}</h2>
          <p className="text-white/60 mb-4">{done.known}/{results.length} {t("carte che sai.")}</p>
          <div className="flex justify-center gap-6 font-num mb-6">
            <span className="flex items-center gap-1"><Zap size={18} style={{ color: "var(--cyan)" }} /> +{done.xp_earned} XP</span>
          </div>
          <div className="space-y-3">
            {unknownCount > 0 && (
              <button onClick={() => { setOnlyUnknown(true); load(true); }} className="w-full py-3 rounded-2xl btn-violet font-heading font-bold bouncy" data-testid="flash-restudy-btn">
                {t("Non la so")} ({unknownCount})
              </button>
            )}
            <button onClick={() => navigate(`/exam/${examId}`)} className="w-full py-3 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="flash-done-back">{t("Torna all'esame")}</button>
          </div>
        </motion.div>
      </div>
    );
  }

  const card = cards[idx];
  const meta = TYPE_META[card.type] || TYPE_META.definizione;
  const MIcon = meta.icon;

  return (
    <div className="space-bg min-h-screen flex flex-col">
      <header className="glass sticky top-0 z-40 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(`/exam/${examId}`)} className="text-white/60 bouncy" data-testid="flash-exit-btn"><X size={22} /></button>
        <span className="font-num text-sm text-white/70">{idx + 1}/{cards.length}</span>
        <span className="flex items-center gap-1 text-xs font-heading" style={{ color: meta.color }}><MIcon size={16} /> {t(meta.label)}</span>
      </header>

      <div className="flex-1 max-w-lg w-full mx-auto px-4 py-6 flex flex-col justify-center">
        <motion.button key={idx + (flipped ? "b" : "f")} initial={{ rotateY: 90, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }}
          onClick={() => setFlipped(!flipped)} className="glass rounded-3xl p-8 min-h-[280px] flex flex-col items-center justify-center text-center bouncy" data-testid="flashcard">
          <MIcon size={32} style={{ color: meta.color }} className="mb-4 opacity-60" />
          <p className="font-heading font-bold text-xl tracking-tight leading-snug"><MathText>{flipped ? card.back : card.front}</MathText></p>
          <span className="mt-6 text-xs text-white/40 flex items-center gap-1"><RotateCw size={12} /> {flipped ? t("fronte") : t("tocca per girare")}</span>
        </motion.button>

        <AnimatePresence>
          {flipped && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 gap-3 mt-6">
              <button onClick={() => mark(false)} className="py-4 rounded-2xl font-heading font-bold bouncy flex items-center justify-center gap-2"
                style={{ background: "rgba(255,51,102,0.18)", border: "2px solid var(--danger)" }} data-testid="flash-dontknow-btn">
                <X size={18} style={{ color: "var(--danger)" }} /> {t("Non la so")}
              </button>
              <button onClick={() => mark(true)} className="py-4 rounded-2xl font-heading font-bold bouncy flex items-center justify-center gap-2"
                style={{ background: "rgba(0,255,127,0.18)", border: "2px solid var(--success)" }} data-testid="flash-know-btn">
                <Check size={18} style={{ color: "var(--success)" }} /> {t("La so")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
